// src/components/reports/precon-report-builder/build-precon-payload.ts
import type { GenerateReportPayload } from "@/components/records/report-generation-dialog/use-generate-report";
import { toFriendlyDate } from "./precon-dates";
import type {
  PreconCover,
  PreconSectionMeta,
  PreconSelectedPhoto,
  PreconSelectedRecord,
  PreconSignatory,
} from "./precon-types";

interface BuildArgs {
  reportName: string;
  headerBanner: string;
  cover: PreconCover;
  orderedSections: PreconSectionMeta[];
  /** The selected records, each carrying all of its photos (taken_at order). */
  records: PreconSelectedRecord[];
  /** Per-photo caption edits, keyed by `image_id` (report-only). */
  captions: Record<string, string>;
}

const keepSignatory = (s?: PreconSignatory): PreconSignatory | undefined =>
  s && s.name.trim() ? s : undefined;

/** Photos of a record, capture-time ascending — the sub-letter / establishing-shot order. */
function orderedPhotos(record: PreconSelectedRecord): PreconSelectedPhoto[] {
  return [...record.photos].sort((a, b) => {
    if (a.taken_at === b.taken_at) return 0;
    if (a.taken_at === null) return 1;
    if (b.taken_at === null) return -1;
    return a.taken_at < b.taken_at ? -1 : 1;
  });
}

/**
 * Resolve a photo's caption: a user edit wins; otherwise the record's
 * **establishing shot** (its first photo by `taken_at`) seeds from the record
 * remark and every other photo defaults to blank. Mirrors the seed rule the
 * UI rows apply, so the payload matches what the user sees.
 */
function resolveCaption(
  record: PreconSelectedRecord,
  photo: PreconSelectedPhoto,
  isEstablishingShot: boolean,
  captions: Record<string, string>,
): string {
  const edited = captions[photo.image_id];
  if (edited !== undefined) return edited;
  return isEstablishingShot ? record.remark : "";
}

export function buildPreconPayload(args: BuildArgs): GenerateReportPayload {
  // Photo-as-unit: every record sends ALL of its photos, each with its
  // per-image caption (may be ""). Captions are report-only — the precon
  // payload omits `override_remarks` (the write-back never fires for precon).
  const records = args.records.map((record) => {
    // `i === 0` is the establishing shot: orderedPhotos sorts by the same
    // compareTakenAt that entriesForRecord uses to assign sub-letters, so the
    // i=0 entry is always the sub-letter-"a" entry. This is equivalent to
    // isEstablishingShot() in derive-precon-sections.ts — that helper operates
    // on DerivedPhotoEntry which is not available here (raw PreconSelectedRecord).
    const photos = orderedPhotos(record).map((photo, i) => ({
      image_id: photo.image_id,
      caption: resolveCaption(record, photo, i === 0, args.captions).trim(),
    }));
    return { id: record.id, photos };
  });

  return {
    records,
    name: args.reportName.trim() || undefined,
    template: "PRECON_SURVEY",
    orientation: "LANDSCAPE",
    format: "PDF",
    template_metadata: {
      header_banner: args.headerBanner,
      cover: {
        ...args.cover,
        prepared_by: keepSignatory(args.cover.prepared_by),
        approved_by: keepSignatory(args.cover.approved_by),
        revisions: args.cover.revisions.map((r) => ({
          ...r,
          date: toFriendlyDate(r.date),
        })),
      },
      sections: args.orderedSections.map((s) => ({
        ...s,
        precon_date: toFriendlyDate(s.precon_date),
      })),
    },
  };
}
